/* ═══════════════════════════════════════════════════════════════
   SearchableSelect — keyed (value/label) searchable single-select.
   Used anywhere the user picks one item from a list. Replaces
   plain <select> per firm UX policy (no dropdown-only controls).

   The menu renders in a portal on <body> (fixed, viewport-clamped,
   flips above the trigger when there's no room below) so it is NEVER
   clipped by panel overflow or transformed ancestors — contextual UI
   always lands on top of everything.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { FocusScope } from '@radix-ui/react-focus-scope';
import { Check, type LucideIcon } from 'lucide-react';
import { rem } from '../lib/rem';
import { Icon } from './ui/Icon';
import { computeMenuPlacement } from './menuPlacement';
import { appLayerProps } from './ui/app-layer';

export interface SelectOption {
  value: string;
  label: string;
  hint?: string; // optional secondary text shown in the row
  group?: string; // optional group heading to show above item
  /** Leading glyph (e.g. the custom-widget icon picker) — rendered before
   *  the label in both the option row and the trigger's selected value. */
  icon?: LucideIcon;
  /** Tree depth (0 = top level) — indents the row so a nested path (a
   *  category tree, say) reads as a hierarchy without repeating every
   *  ancestor's name in the label itself (the bulk "move to" picker). */
  indent?: number;
  /** Right-aligned, muted, tabular-numeral count — a module/item count
   *  alongside the label (the bulk "move to" picker). Separate from `hint`
   *  so a caller can show both a trailing hint AND a count. */
  count?: number;
}

export interface SearchableSelectProps {
  value: string; // current value (option.value) — single-select mode
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string; // shown when nothing selected
  searchPlaceholder?: string; // shown in the search input
  clearable?: boolean; // show "Clear selection" (default true)
  disabled?: boolean;
  style?: React.CSSProperties; // applied to trigger
  /** Either a fixed string or a function of the trimmed search query — lets
   *  a caller (e.g. the bulk "move to" picker) distinguish "no options at
   *  all" from "none match what you typed". A caller passing a plain
   *  string keeps the old behavior byte-for-byte. */
  noResultsText?: string | ((query: string) => string);
  autoOpenOnFocus?: boolean; // opens when focused via keyboard
  /** Multi-select mode: the menu stays open, rows carry checkmarks, and the
   *  trigger summarises the picks. Single-select behaviour is untouched when
   *  this is off — `value`/`onChange` are simply ignored in multi mode in
   *  favour of `values`/`onChangeMulti`. */
  multiple?: boolean;
  values?: string[];
  onChangeMulti?: (values: string[]) => void;
  ariaLabel?: string;
  /** Stamps the portal'd menu with `data-owner={ownerId}` so a caller that
   *  dismisses on document-mousedown-outside of some OTHER element (e.g.
   *  Fields.tsx's per-row edit state) can treat a mousedown inside this
   *  select's body-level portal as "inside" that owning element even
   *  though the portal itself sits outside its DOM subtree. Optional —
   *  omit when nothing outside this component needs to recognize it. */
  ownerId?: string;
  /** Ties an inline error elsewhere in the form to this control
   *  (DESIGN §7 — errors tied to fields with aria-describedby). */
  describedBy?: string;
  /** Type-to-create (Module Category / Tags pickers, and any future picker
   *  that wants it): when the typed query matches no option's label
   *  exactly, a "Create …" row appears above the list. Selecting it calls
   *  `onCreate` with the trimmed query and clears the search box — it does
   *  NOT select a value itself, since a multi-level create (a typed
   *  category path) has no single new option to select; the caller reacts
   *  (creates, then sets `value`/`values` once the result is known). The
   *  menu stays open so a freshly created option appears the moment the
   *  caller's `options` prop updates. */
  creatable?: boolean;
  /** Row label for the typed query. Default `Create "query"`. */
  creatableLabel?: (query: string) => string;
  onCreate?: (query: string) => void;
  /** Renders the search input + option list directly in flow — always
   *  open, no trigger button, no portal, no fixed positioning (the bulk
   *  "move to" picker, ModuleCategorySelect's `inline` mode). Reuses every
   *  other code path (filtering, keyboard, creatable, ARIA) unchanged.
   *  Default (omitted): the existing closed dropdown, byte-for-byte
   *  unchanged. */
  inline?: boolean;
  /** Helper line shown under the search input, inline mode only, when
   *  `creatable` is on — explains the type-to-create row the list already
   *  carries. Ignored outside inline mode or without `creatable`. */
  createHint?: string;
}

const MENU_GAP = 4; // gutter between menu, trigger and viewport edge
const MENU_MAX_W = 480; // sane cap when growing to the longest row

/** Option-row inset. Longhands only — never a `padding` shorthand beside an
 *  undefined `paddingLeft` (React emits an empty declaration and the browser
 *  drops the whole padding). Horizontal inset matches the search field. */
export function optionRowPadding(indent?: number): React.CSSProperties {
  return {
    paddingBlock: rem(7),
    paddingInline: indent ? `${rem(7 + indent * 14)} var(--space-3)` : 'var(--space-3)',
  };
}

export function SearchableSelect({
  value,
  options,
  onChange,
  placeholder = '— Select —',
  searchPlaceholder = 'Search…',
  clearable = true,
  disabled = false,
  style,
  noResultsText = 'No matches',
  autoOpenOnFocus = false,
  multiple = false,
  values,
  onChangeMulti,
  ariaLabel,
  ownerId,
  describedBy,
  creatable = false,
  creatableLabel,
  onCreate,
  inline = false,
  createHint,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [focusIdx, setFocusIdx] = useState(0);
  const [pos, setPos] = useState<{ left: number; top: number; width: number; maxH: number } | null>(
    null
  );
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);
  const picked = multiple ? values || [] : [];
  const pickedSet = React.useMemo(() => new Set(picked), [picked.join(',')]);
  const multiLabel =
    picked.length === 0
      ? ''
      : picked.length === 1
        ? options.find((o) => o.value === picked[0])?.label || picked[0]
        : `${picked.length} selected`;

  // Position the portal menu off the trigger: below by default, above when the
  // viewport has no room, clamped horizontally. Fixed-position + portal = never
  // clipped by overflow or skewed by transformed ancestors.
  const place = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    // Natural content height: measure the mounted menu uncapped, then restore.
    const el = menuRef.current;
    let contentHeight = 0;
    if (el) {
      const prev = el.style.maxHeight;
      el.style.maxHeight = 'none';
      contentHeight = el.scrollHeight;
      el.style.maxHeight = prev;
    }
    const longest = options.reduce(
      (m, o) => Math.max(m, o.label.length + (o.hint?.length ?? 0)),
      0
    );
    const p = computeMenuPlacement({
      triggerRect: r,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      contentHeight: contentHeight || Number.MAX_SAFE_INTEGER,
      contentWidth: longest * 8 + 64,
      gutter: MENU_GAP,
      maxWidth: MENU_MAX_W,
    });
    setPos((cur) =>
      cur &&
      cur.left === p.left &&
      cur.top === p.top &&
      cur.width === p.width &&
      cur.maxH === p.maxHeight
        ? cur
        : { left: p.left, top: p.top, width: p.width, maxH: p.maxHeight }
    );
  };

  // Re-measure on open, after the menu mounts, and when the rows change.
  useLayoutEffect(() => {
    if (open) place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pos === null, query, options.length]);

  // Inline mode has no trigger/open toggle — the search input autofocuses
  // once, on mount, same spirit as the dropdown's own open-time autofocus.
  useEffect(() => {
    if (!inline) return;
    setTimeout(() => inputRef.current?.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setFocusIdx(0);
    setTimeout(() => inputRef.current?.focus(), 0);
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !menuRef.current?.contains(t)) setOpen(false);
    };
    // Scrolling/resizing the page under a fixed menu detaches it — close instead.
    const onScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return; // menu's own scroll is fine
      setOpen(false);
    };
    const onResize = () => place();
    // Esc closes the menu wherever focus sits (capture, so it lands even when
    // a host Radix layer is listening) — the host stays open, see ui/dialog.tsx.
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onEsc, true);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('keydown', onEsc, true);
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  // Bug fix: a host Radix Dialog's scroll lock (react-remove-scroll) adds a
  // BUBBLE-phase `wheel`/`touchmove` listener on `document` that
  // preventDefaults any event whose target sits outside the dialog's own
  // DOM subtree — this body-portaled menu is always "outside" from the
  // dialog's point of view, so the wheel died while dragging the scrollbar
  // still worked (nothing in this file handled wheel at all before this). A
  // plain NATIVE listener on the menu root fires on the way up, before the
  // event ever reaches `document` — stopping it there, regardless of host.
  // Never calls preventDefault: the browser's own scroll on the list still
  // happens; only propagation PAST this node is stopped. No-op outside a
  // dialog (nothing else listens for wheel on document there).
  //
  // `pos` is in the deps, not just `open`: the menu only actually MOUNTS
  // (portal + `menuRef` attaches) once `place()` has resolved `pos` — a
  // DIFFERENT commit than the one where `open` first flips true (that
  // commit renders with `pos` still null, so nothing is in the DOM yet).
  // An effect keyed on `[open]` alone ran exactly once, read a still-null
  // `menuRef.current`, and never got a second chance — the listener was
  // never attached at all. Re-running when `pos` changes (every reposition,
  // not just the first) means the cleanup/re-attach is a no-op cost-wise
  // (same node) but guarantees attachment the moment the node exists.
  useEffect(() => {
    if (!open) return;
    const menu = menuRef.current;
    if (!menu) return;
    const stop = (e: Event) => e.stopPropagation();
    menu.addEventListener('wheel', stop);
    menu.addEventListener('touchmove', stop);
    return () => {
      menu.removeEventListener('wheel', stop);
      menu.removeEventListener('touchmove', stop);
    };
  }, [open, pos]);

  const filtered = query
    ? options.filter(
        (o) =>
          o.label.toLowerCase().includes(query.toLowerCase()) ||
          (o.hint && o.hint.toLowerCase().includes(query.toLowerCase()))
      )
    : options;

  const trimmedQuery = query.trim();
  // A caller passing a function (the bulk "move to" picker distinguishing
  // "no categories at all" from "none match what you typed") gets it called
  // with the trimmed query; a plain string renders unchanged — byte-for-byte
  // the same for every existing caller.
  const noResultsMessage =
    typeof noResultsText === 'function' ? noResultsText(trimmedQuery) : noResultsText;
  const showCreate =
    creatable &&
    trimmedQuery.length > 0 &&
    !options.some((o) => o.label.toLowerCase() === trimmedQuery.toLowerCase());

  function commit(v: string) {
    if (multiple) {
      // Toggle and keep the menu open — picking several values in a row is
      // the whole point of multi mode.
      onChangeMulti?.(pickedSet.has(v) ? picked.filter((p) => p !== v) : [...picked, v]);
      return;
    }
    onChange(v);
    setOpen(false);
  }

  /** The "Create …" row. Never selects — see the prop doc above. */
  function commitCreate() {
    if (!trimmedQuery) return;
    setQuery('');
    onCreate?.(trimmedQuery);
  }

  // The "Create …" row renders ABOVE the option list (see below), so when
  // it's showing it occupies virtual index 0 and every filtered option
  // shifts down by one — the same arrow-key/Enter reach as any other row,
  // not just a mouse target or the empty-list fallback. Byte-for-byte the
  // same indexing as before when `showCreate` is false (creatable prop
  // absent or unmatched): this component is used app-wide.
  const createOffset = showCreate ? 1 : 0;

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIdx((i) => Math.min(i + 1, filtered.length - 1 + createOffset));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (showCreate && focusIdx === 0) {
        commitCreate();
        return;
      }
      const opt = filtered[focusIdx - createOffset];
      // The create row has no index of its own in the FILTERED list — it
      // still fires on Enter when nothing there would (an empty list, or
      // the creatable row is the only thing showing).
      if (opt) commit(opt.value);
      else if (showCreate) commitCreate();
    } else if (e.key === 'Escape' && !inline) {
      e.preventDefault();
      // Esc dismisses THIS menu only — without stopping the event a host
      // Radix Dialog would also close underneath it. Inline mode has no
      // floating menu to dismiss separately — Esc falls through untouched
      // so the host dialog's own Esc-to-close still fires (DESIGN §7).
      e.stopPropagation();
      setOpen(false);
    } else if (e.key === 'Tab' && !inline && ref.current?.closest('[role="dialog"]')) {
      // Side effect B (tags-focus-fix brief): while this menu is open INSIDE
      // a host Radix Dialog/Sheet, that host's own focus trap is paused (see
      // the FocusScope comment below) — left alone, a native Tab from this
      // search input falls through to document order and can land on the
      // dialog's own end-of-body focus guard and then keep going into the
      // real page behind it (aria-hidden, inert, but still focusable/typeable
      // — a bug). Close the menu and move focus to the trigger BEFORE Tab's
      // default action runs, so the browser's native Tab/Shift+Tab continues
      // FROM the trigger — which lives inside the host, not body-end —
      // instead of from this about-to-unmount portal. Not prevented: the
      // browser's own default still fires and performs the actual tab step.
      // Scoped to dialog hosts only (checked via the TRIGGER's real DOM
      // position, not this body-portalled menu's) — outside a dialog this
      // menu never pauses anything, so Tab here must stay exactly what it
      // was before 7178435: untouched, native default.
      setOpen(false);
      triggerRef.current?.focus();
    }
  }

  const trigger: React.CSSProperties = {
    width: '100%',
    padding: `${rem(7)} ${rem(10)}`,
    borderRadius: rem(5),
    border: '1px solid var(--bd)',
    background: disabled ? 'var(--sa)' : 'var(--ib)',
    color: 'var(--tx)',
    fontSize: 'var(--font-size-base)',
    cursor: disabled ? 'default' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: rem(34),
    ...style,
  };

  // Group-aware render: if any option has a `group`, break by group in filtered list
  const hasGroups = options.some((o) => o.group);

  // Inline mode: no trigger, no portal, no positioning wrapper — the host
  // (the bulk "move to" picker) sizes and borders the box itself; this
  // renders the search input + option list directly in flow.
  if (inline) {
    return renderDropdown();
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        ref={triggerRef}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        onClick={() => {
          if (!disabled) setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (
            !open &&
            (e.key === 'Enter' || e.key === ' ' || (autoOpenOnFocus && e.key === 'ArrowDown'))
          ) {
            e.preventDefault();
            setOpen(true);
          } else if (open) onKey(e);
        }}
        style={trigger}
      >
        <span
          style={{
            flex: 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: rem(6),
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: (multiple ? picked.length > 0 : !!selected) ? 'var(--tx)' : 'var(--tm)',
          }}
        >
          {!multiple && selected?.icon && <Icon icon={selected.icon} size="sm" />}
          {multiple ? multiLabel || placeholder : selected ? selected.label : placeholder}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden
          style={{
            marginLeft: rem(6),
            flexShrink: 0,
            color: 'var(--tm)',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform .15s ease',
          }}
        >
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {renderDropdown()}
    </div>
  );

  // ─── Shared pieces — the search input and the option list are IDENTICAL
  // markup whether floating (the dropdown's body-portal) or inline (always
  // open, in flow, no trigger/portal) — one filtering/keyboard/creatable/
  // ARIA implementation, never two. Defined as plain variables (not
  // components) so they close over this render's state exactly once. ───

  function renderInputBlock() {
    return (
      <div style={{ padding: `${rem(6)} var(--space-3)`, borderBottom: '1px solid var(--bd)' }}>
        <input
          ref={inputRef}
          value={query}
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value);
            setFocusIdx(0);
          }}
          onKeyDown={onKey}
          placeholder={searchPlaceholder}
          aria-label={ariaLabel || searchPlaceholder}
          aria-describedby={describedBy}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            width: '100%',
            padding: `${rem(5)} var(--space-2)`,
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--bd)',
            background: disabled ? 'var(--sa)' : 'var(--ib)',
            color: disabled ? 'var(--tm)' : 'var(--tx)',
            fontSize: 'calc(var(--font-size-base) * 12 / 13)',
            outline: 'none',
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
        {inline && creatable && createHint && (
          <p
            style={{
              margin: `${rem(4)} 0 0`,
              fontSize: 'var(--font-size-xs)',
              color: 'var(--tm)',
            }}
          >
            {createHint}
          </p>
        )}
      </div>
    );
  }

  function renderOptionRows() {
    return (
      <div
        aria-disabled={disabled || undefined}
        style={{
          overflow: 'auto',
          flex: 1,
          minHeight: 0,
          overscrollBehavior: 'contain',
          // Rows go inert AND visibly muted while `disabled` (item 5,
          // modules-polish fix-up) — e.g. the bulk category picker while a
          // typed path is being created. `pointerEvents: none` blocks
          // mousedown-commit; the input above is natively `disabled` too,
          // so the keyboard path (its own onKeyDown) is severed as well.
          ...(disabled ? { pointerEvents: 'none', opacity: 0.55 } : {}),
        }}
      >
        {clearable && (multiple ? picked.length > 0 : !!selected) && (
          <div
            onMouseDown={(e) => {
              // Commit on mousedown, not click — a host that dismisses
              // its own UI on mousedown-outside (Fields.tsx) can unmount
              // this portal before a click ever fires (Josh 2026-09-28
              // select-fields-never-save bug). preventDefault keeps
              // focus off the row so it doesn't steal it from the trigger.
              e.preventDefault();
              if (multiple) onChangeMulti?.([]);
              else commit('');
            }}
            style={{
              padding: `${rem(7)} var(--space-3)`,
              fontSize: 'calc(var(--font-size-base) * 12 / 13)',
              color: 'var(--tm)',
              cursor: 'pointer',
              fontStyle: 'italic',
              borderBottom: '1px solid var(--bd)',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            Clear selection
          </div>
        )}
        {showCreate && (
          <div
            role="option"
            aria-selected={false}
            onMouseEnter={() => setFocusIdx(0)}
            onMouseDown={(e) => {
              // Same mousedown-commit timing as every other row here —
              // a host that dismisses on mousedown-outside must never
              // race this.
              e.preventDefault();
              commitCreate();
            }}
            style={{
              padding: `${rem(7)} var(--space-3)`,
              fontSize: 'calc(var(--font-size-base) * 12 / 13)',
              color: 'var(--ac)',
              fontWeight: 600,
              cursor: 'pointer',
              borderBottom: '1px solid var(--bd)',
              background: focusIdx === 0 ? 'var(--sa)' : 'transparent',
            }}
          >
            {(creatableLabel || ((q: string) => `Create "${q}"`))(trimmedQuery)}
          </div>
        )}
        {filtered.length === 0 && !showCreate && (
          <div
            style={{
              padding: 'var(--space-4) var(--space-3)',
              fontSize: 'calc(var(--font-size-base) * 12 / 13)',
              color: 'var(--tm)',
              textAlign: 'center',
            }}
          >
            {noResultsMessage}
          </div>
        )}
        {(() => {
          let lastGroup: string | undefined;
          return filtered.map((o, i) => {
            const showGroup = hasGroups && o.group !== lastGroup;
            lastGroup = o.group;
            const isSel = multiple ? pickedSet.has(o.value) : o.value === value;
            const isFocused = i + createOffset === focusIdx;
            return (
              <React.Fragment key={o.value || `__${i}`}>
                {showGroup && o.group && (
                  <div
                    style={{
                      padding: 'var(--space-2) var(--space-3) var(--space-1)',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 700,
                      color: 'var(--tm)',
                      textTransform: 'uppercase',
                      letterSpacing: '.05em',
                    }}
                  >
                    {o.group}
                  </div>
                )}
                <div
                  role="option"
                  aria-selected={isSel}
                  onMouseEnter={() => setFocusIdx(i + createOffset)}
                  onMouseDown={(e) => {
                    // Commit on mousedown — see the Clear-selection
                    // handler above for why click is too late here.
                    e.preventDefault();
                    commit(o.value);
                  }}
                  style={{
                    ...optionRowPadding(o.indent),
                    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                    cursor: 'pointer',
                    fontWeight: isSel ? 600 : 400,
                    color: isSel ? 'var(--ac)' : 'var(--tx)',
                    background: isFocused
                      ? 'var(--sa)'
                      : isSel
                        ? 'rgba(var(--acr),0.06)'
                        : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                  }}
                >
                  {multiple && (
                    <span
                      aria-hidden
                      style={{
                        width: rem(13),
                        height: rem(13),
                        flexShrink: 0,
                        borderRadius: rem(3),
                        border: `1px solid ${isSel ? 'var(--ac)' : 'var(--bd)'}`,
                        background: isSel ? 'var(--ac)' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {isSel && (
                        <svg width="9" height="9" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M3 8.5l3.5 3.5L13 5"
                            stroke="var(--color-text-inverse)"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                  )}
                  {/* Inline mode only (the bulk "move to" picker) — the floating
                      dropdown already signals selection with bold + accent text,
                      unchanged for every existing caller. */}
                  {!multiple && isSel && inline && (
                    <Icon icon={Check} size="sm" aria-hidden="true" style={{ flexShrink: 0 }} />
                  )}
                  {o.icon && <Icon icon={o.icon} size="sm" />}
                  <span
                    style={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {o.label}
                  </span>
                  {o.hint && (
                    <span
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--tm)',
                        flexShrink: 0,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {o.hint}
                    </span>
                  )}
                  {o.count != null && (
                    <span
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--tm)',
                        flexShrink: 0,
                        fontVariantNumeric: 'tabular-nums',
                        minWidth: rem(20),
                        textAlign: 'right',
                      }}
                    >
                      {o.count}
                    </span>
                  )}
                </div>
              </React.Fragment>
            );
          });
        })()}
      </div>
    );
  }

  function renderDropdown() {
    if (inline) {
      // No trigger, no portal, no fixed positioning, no FocusScope (nothing
      // here ever steals focus away from the host dialog's own trap — it's
      // rendered IN FLOW, inside the host's own DOM subtree, not body-
      // portalled outside it). Esc is handled entirely by `onKey`'s
      // `!inline` guard above: untouched, so it bubbles to the host dialog.
      return (
        <div
          ref={menuRef}
          role="listbox"
          aria-multiselectable={multiple || undefined}
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}
        >
          {renderInputBlock()}
          {renderOptionRows()}
        </div>
      );
    }
    if (!open || disabled || !pos) return null;
    return createPortal(
      // A host Radix Dialog/Sheet wraps its content in a TRAPPED FocusScope
      // that watches document-level focusin/focusout and yanks focus back
      // inside itself the instant it lands anywhere outside — and this menu,
      // body-portalled, is always "outside" from the dialog's point of view,
      // so the search input could never keep focus. Mounting ANY FocusScope
      // pauses whichever scope is active on Radix's shared stack (the same
      // trick Radix's own Popover-inside-Dialog relies on) and resumes it on
      // unmount — `trapped={false}` here because a single search input has
      // nothing else inside the menu to Tab-cycle through; the pause/resume
      // and the default mount/unmount auto-focus (search input in, trigger
      // back out) are what we need, not an inner trap.
      <FocusScope
        asChild
        trapped={false}
        onUnmountAutoFocus={(e) => {
          // Side effect A (tags-focus-fix brief): Radix's default here
          // is `focus(previouslyFocusedElement ?? document.body)` — right
          // when the menu closed while STILL holding focus (Esc; a commit
          // whose row handler preventDefaults the mousedown so focus never
          // left the search input) — document.activeElement reverts to
          // <body> the instant this portal's focused input is removed,
          // and we WANT the trigger to reclaim it then. But if the user
          // has already clicked into some other real element while the
          // menu was open, that element already won focus for a reason —
          // Radix's un-prevented default would yank it straight back to
          // this trigger a tick later and swallow the click. Only let the
          // default run when nothing else has claimed focus yet.
          if (document.activeElement && document.activeElement !== document.body) {
            e.preventDefault();
          }
        }}
      >
        <div
          ref={menuRef}
          role="listbox"
          aria-multiselectable={multiple || undefined}
          // Marks the portal'd menu so host surfaces with their own
          // mousedown-outside/interact-outside dismissal (Radix Dialog,
          // context menus) can exempt clicks inside it — the menu lives
          // on document.body, outside any host's DOM subtree. Mirrors
          // the `data-colorpicker-panel` exemption pattern
          // (ui/color-picker.tsx) — see ui/dialog.tsx DialogContent.
          data-searchable-select-menu
          {...appLayerProps}
          {...(ownerId ? { 'data-owner': ownerId } : {})}
          style={{
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            width: pos.width,
            zIndex: 2147483000,
            // Radix's modal Dialog sets `pointer-events: none` on <body>;
            // this menu is a body-level portal, so it would inherit that and
            // become unclickable inside any dialog. Opt back in explicitly.
            pointerEvents: 'auto',
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            maxHeight: pos.maxH,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {renderInputBlock()}
          {renderOptionRows()}
        </div>
      </FocusScope>,
      document.body
    );
  }
}

export default SearchableSelect;
